<template>
	<div class="newsletter-selection-item" @click="$emit('click')">
		<div class="preview-container">
			<div class="iframe-wrapper">
				<iframe v-if="compiledHtml" ref="previewIframe" class="newsletter-preview-iframe" :srcdoc="compiledHtmlWithStyles"></iframe>
				<div v-else class="no-preview">
					<p>{{ getString('no_preview_available') }}</p>
				</div>
			</div>
		</div>
		<div class="newsletter-info">
			<p class="newsletter-subject">{{ newsletter.subject || getString('no_subject') }}</p>
			<div class="newsletter-meta">
				<span class="newsletter-status">{{ getStatusText() }}</span>
				<span class="newsletter-date">{{ getDateText() }}</span>
			</div>
		</div>
	</div>
</template>

<script>
import mjml2html from "mjml-browser";
import {useLanguageStore} from "@/shared/stores";

export default {
	setup() {
		let languageStore = useLanguageStore();
		return {
			getString: languageStore.getString,
		};
	},
	name: "NewsletterSelectionItem",
	props: {
		newsletter: {
			type: Object,
			required: true
		}
	},
	emits: ['click'],
	computed: {
		compiledHtml() {
			const version = this.newsletter.draft_version || this.newsletter.current_version;
			if (!version) {
				return null;
			}
			try {
				const result = mjml2html(version);
				return result.html;
			} catch (e) {
				console.error('Failed to compile MJML:', e);
				return null;
			}
		},
		compiledHtmlWithStyles() {
			if (!this.compiledHtml) {
				return null;
			}
			// Inject CSS to hide scrollbar in the iframe content
			const styleTag = `
				<style>
					body {
						overflow: hidden !important;
					}
					::-webkit-scrollbar {
						display: none !important;
					}
					* {
						-ms-overflow-style: none !important;
						scrollbar-width: none !important;
					}
				</style>
			`;
			// Insert style tag before closing head tag
			return this.compiledHtml.replace('</head>', `${styleTag}</head>`);
		}
	},
	methods: {
		formatDate(dateString) {
			if (!dateString) return '';
			const date = new Date(dateString);
			return date.toLocaleDateString();
		},
		getStatusText() {
			if (this.newsletter.status === 'scheduled' && this.newsletter.scheduled_at) {
				return this.getString('scheduled_for', [this.formatDate(this.newsletter.scheduled_at)]);
			}
			return this.getString(this.newsletter.status);
		},
		getDateText() {
			if (this.newsletter.status === 'scheduled' && this.newsletter.scheduled_at) {
				return "";
			}
			return this.formatDate(this.newsletter.created_at);
		}
	}
}
</script>

<style scoped lang="scss">
.newsletter-selection-item {
	display: flex;
	flex-direction: row;
	border: 1px solid var(--color-border-2);
	border-radius: 13px;
	overflow: hidden;
	cursor: pointer;
	transition: all 0.2s;
	background: var(--color-bg-primary-2);
	height: 200px;

	&:hover {
		border-color: var(--color-primary);
		box-shadow: 0 4px 12px var(--color-box-shadow-2);
	}
}

.preview-container {
	position: relative;
	flex-shrink: 0;
	width: 150px;
	height: 200px;
	overflow: hidden;
	background: #FFFFFF;
	display: flex;
	align-items: flex-start;
	justify-content: flex-start;
}

.iframe-wrapper {
	width: 600px;
	height: 800px;
	transform: scale(0.25);
	transform-origin: top left;
	position: absolute;
	top: 0;
	left: 0;
}

.newsletter-preview-iframe {
	width: 100%;
	height: 100%;
	border: none;
	pointer-events: none;
	overflow: hidden;

	&::-webkit-scrollbar {
		display: none;
	}

	-ms-overflow-style: none;
	scrollbar-width: none;
}

.no-preview {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;

	p {
		color: var(--color-text-2);
		font-size: 14px;
	}
}

.newsletter-info {
	flex: 1;
	padding: 8px 16px;
	display: flex;
	flex-direction: column;
	gap: 8px;
	justify-content: space-between;
}

.newsletter-subject {
	font-weight: 600;
	font-size: 15px;
	color: var(--color-txt-1);
	margin: 0;
	@include truncateString(4);
}

.newsletter-meta {
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 12px;
	font-size: 13px;

	.newsletter-status {
		color: var(--color-txt-2);
		font-size: 12px;
	}

	.newsletter-date {
		color: var(--color-txt-2);
		font-size: 12px;
	}
}

@media (max-width: 1350px) {
	.newsletter-selection-item {
		flex-direction: column;
		height: auto;
	}

	.preview-container {
		width: 100%;
		height: 200px;
		display: block;
		position: relative;
		overflow: hidden;
		background: #FFFFFF;
	}

	.iframe-wrapper {
		width: 100%;
		height: 800px;
		transform: scale(0.25);
		transform-origin: top left;
		position: absolute;
		top: 0;
		left: 0;
	}

	.newsletter-preview-iframe {
		width: 400%;
		height: 100%;
	}

	.newsletter-info {
		padding: 16px;
	}
}

@media (max-width: 720px) {
	.newsletter-selection-item {
		flex-direction: row;
		height: 200px;
	}

	.preview-container {
		width: 150px;
		height: 200px;
	}

	.iframe-wrapper {
		width: 600px;
		height: 800px;
		transform: scale(0.25);
		transform-origin: top left;
		position: absolute;
		top: 0;
		left: 0;
	}

	.newsletter-preview-iframe {
		width: 100%;
		height: 100%;
	}
}

@media (max-width: 420px) {
	.newsletter-selection-item {
		flex-direction: column;
		height: auto;
	}

	.preview-container {
		width: 100%;
		height: 200px;
	}

	.iframe-wrapper {
		width: 100%;
		height: 800px;
		transform: scale(0.25);
		transform-origin: top left;
		position: absolute;
		top: 0;
		left: 0;
	}

	.newsletter-preview-iframe {
		width: 400%;
		height: 100%;
	}
}

</style>
